import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  AppMessageSeverities,
  DEFAULT_COMPANY_FEATURE_FLAGS,
  getCompanyRequiredAuthMethod,
} from '@/types';
import { Button, Dialog, DialogActions, DialogContent, Divider } from '@/ui';
import { deriveChangedCompanyDomains, trimList } from '@/utils';

import type { Company, CompanyForm } from '@/types';
import type { FC } from 'react';

import CompanyAccess from '@/components/Company/Info/Edit/Access';
import CompanyFeatures from '@/components/Company/Info/Edit/Features';
import VisionAndGoals from '@/components/Company/Info/Edit/Goals';
import CompanyProfile from '@/components/Company/Info/Edit/Profile';
import {
  getCompanyMutationErrorMessage,
  getCompanyMutationFieldErrors,
  useGetCompanyQuery,
  useUpdateCompanyMutation,
} from '@/store/api/companyApi';
import { useAppDispatch } from '@/store/hooks';
import { setAppMessage } from '@/store/slices/appSlice';

const AdminCompanyEditDialog: FC<{
  company: Company;
  onClose: () => void;
}> = ({ company: currentCompany, onClose }) => {
  const dispatch = useAppDispatch();
  const [updateCompany, { isLoading: loading }] = useUpdateCompanyMutation();
  const { refetch: refetchCompany } = useGetCompanyQuery(currentCompany.id);
  const [company, setCompany] = useState(currentCompany);
  const appliedSnapshotRef = useRef<Company | null>(null);
  const failedSaveRef = useRef(false);
  const saveInFlightRef = useRef(false);

  const onCompanyChange = (updated: Company) => setCompany(updated);

  const defaultValues: CompanyForm = {
    avatarUrl: '',
    culture: '',
    adminEmails: '',
    contractedSeats: null,
    disallowedEmails: '',
    domainMembersSelfPay: false,
    domains: '',
    emails: '',
    industry: '',
    linkedin: '',
    location: '',
    name: '',
    pocEmail: '',
    pocName: '',
    featureFlags: { ...DEFAULT_COMPANY_FEATURE_FLAGS },
    sessionTimeoutLength: null,
    required_auth_method: null,
    vision: '',
    website: '',
  };
  const {
    control,
    formState: { isDirty },
    handleSubmit,
    setError,
    reset,
  } = useForm({
    defaultValues,
    mode: 'onBlur',
  });

  useEffect(() => {
    const applied = appliedSnapshotRef.current;
    const draftGoals = company.goals.length ? company.goals : [''];
    const originalGoals = applied?.goals.length ? applied.goals : [''];
    const goalsDirty =
      draftGoals.some((goal, index) => goal !== originalGoals[index]) ||
      draftGoals.length !== originalGoals.length;

    // RHF does not own goals. Never adopt a refreshed baseline while either
    // the form or the goal draft still belongs to the previous snapshot.
    if (
      applied?.id === currentCompany.id &&
      (isDirty ||
        goalsDirty ||
        failedSaveRef.current ||
        saveInFlightRef.current)
    )
      return;

    failedSaveRef.current = false;
    appliedSnapshotRef.current = currentCompany;
    setCompany(currentCompany);
    reset({
      ...currentCompany,
      adminEmails: currentCompany.adminEmails.join(','),
      contractedSeats: currentCompany.contractedSeats ?? null,
      disallowedEmails: currentCompany.disallowedEmails.join(','),
      domainMembersSelfPay: currentCompany.domainMembersSelfPay ?? false,
      domains: currentCompany.domains.join(','),
      emails: currentCompany.emails.join(','),
      featureFlags: {
        ...DEFAULT_COMPANY_FEATURE_FLAGS,
        ...currentCompany.featureFlags,
      },
      sessionTimeoutLength: currentCompany.sessionTimeoutLength ?? null,
      required_auth_method: getCompanyRequiredAuthMethod(currentCompany),
    });
    // Only a new company snapshot should trigger adoption, not draft edits.
  }, [currentCompany]);

  const onSave = () => {
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;

    handleSubmit(
      async (data) => {
        try {
          const parsedDomains = deriveChangedCompanyDomains(
            data.domains,
            appliedSnapshotRef.current!.domains,
          );
          if (!parsedDomains.success) {
            setError('domains', {
              message: parsedDomains.error,
              type: 'validate',
            });
            return;
          }

          const payload = {
            adminEmails: trimList(data.adminEmails),
            avatarUrl: data.avatarUrl,
            contractedSeats: data.contractedSeats ?? null,
            culture: data.culture,
            disallowedEmails: trimList(data.disallowedEmails),
            domainMembersSelfPay: data.domainMembersSelfPay ?? false,
            ...(parsedDomains.domains
              ? { domains: parsedDomains.domains }
              : {}),
            emails: trimList(data.emails),
            featureFlags: data.featureFlags,
            goals: company.goals.filter((goal) => !!goal.trim()),
            industry: data.industry,
            linkedin: data.linkedin,
            location: data.location,
            name: data.name,
            pocEmail: data.pocEmail,
            pocName: data.pocName,
            requiredAuthMethod: data.required_auth_method || null,
            ...((data.sessionTimeoutLength ?? null) !==
            (appliedSnapshotRef.current?.sessionTimeoutLength ?? null)
              ? { sessionTimeoutLength: data.sessionTimeoutLength }
              : {}),
            vision: data.vision,
            website: data.website,
          };

          await updateCompany({
            id: company.id,
            data: payload,
          }).unwrap();
          onClose();
        } catch (e) {
          failedSaveRef.current = true;
          const errors = getCompanyMutationFieldErrors(e);
          type FormKeys = keyof CompanyForm;
          const formFields = Object.keys(defaultValues);
          const hasFieldErrors = formFields.some((field) => errors[field]);

          formFields.forEach((field) => {
            const errorValues = errors[field];
            if (errorValues) {
              setError(field as FormKeys, {
                message: errorValues.join(','),
                type: 'custom',
              });
            }
          });

          if (!hasFieldErrors) {
            dispatch(
              setAppMessage({
                message: getCompanyMutationErrorMessage(e),
                open: true,
                severity: AppMessageSeverities.Error,
              }),
            );
          }

          try {
            const refreshedCompany = await refetchCompany().unwrap();
            setCompany((draft) => ({
              ...draft,
              b2b_organization_id: refreshedCompany.b2b_organization_id,
              b2bOrganizationId: refreshedCompany.b2bOrganizationId,
              b2bProvisioningMessage: refreshedCompany.b2bProvisioningMessage,
              b2bProvisioningStatus: refreshedCompany.b2bProvisioningStatus,
            }));
          } catch {
            // Preserve the mutation error if the persistent-state refresh fails.
          }
        } finally {
          saveInFlightRef.current = false;
        }
      },
      () => {
        saveInFlightRef.current = false;
      },
    )();
  };

  const onDialogClose = () => {
    if (!saveInFlightRef.current && !loading) onClose();
  };

  return (
    <Dialog maxWidth="md" fullWidth open onClose={onDialogClose}>
      <DialogContent>
        <>
          <CompanyProfile companyId={company.id} control={control} />
          <Divider className="my-8" />
        </>
        <VisionAndGoals
          company={company}
          control={control}
          onCompanyChange={onCompanyChange}
        />
        <>
          <Divider className="my-8" />
          <CompanyAccess company={company} control={control} />
          <Divider className="my-8" />
          <CompanyFeatures control={control} />
        </>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button disabled={loading} variant="outlined" onClick={onDialogClose}>
          Cancel
        </Button>
        <Button loading={loading} variant="contained" onClick={onSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminCompanyEditDialog;
